import React, { memo, useCallback, useEffect, useState } from 'react';

import { AxiosError } from 'axios';

import { areEqual } from 'Utils/equalityChecks';
import { Api } from 'Utils/api';

import {
  ReportProfile,
  ReportSchedule,
  ReportRecipient,
  PipelineStageConfig,
  CustomFieldConfig,
  NotesConfig,
  Pipeline,
  CustomField,
} from './types';

import classes from './reportProfiles.module.css';

function getApiErrorMessage(err: unknown, fallback: string): string {
  const errors = (err as AxiosError<{ errors: Record<string, string[]> }>)?.response?.data?.errors;
  if (errors) {
    return Object.values(errors).flat().join(' ');
  }
  return fallback;
}

interface ReportProfilesProps {
  locationId: string;
  ssoToken: string;
}

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
];

const DAYS_OF_WEEK = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const TIME_RANGE_PRESETS = [
  { label: '24 hours', value: 24 },
  { label: '48 hours', value: 48 },
  { label: '72 hours', value: 72 },
  { label: '1 week', value: 168 },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DEFAULT_SCHEDULE_FORM: ReportSchedule = {
  schedule_type: 'daily',
  time: '09:00',
  timezone: 'America/New_York',
  day_of_week: null,
  day_of_month: null,
  is_active: true,
};

const DEFAULT_NOTES_CONFIG: NotesConfig = { time_range_hours: 24 };

const normalizeScheduleForm = (form: ReportSchedule): ReportSchedule => {
  if (form.schedule_type === 'weekly') {
    return { ...form, day_of_month: null };
  }
  if (form.schedule_type === 'monthly') {
    return { ...form, day_of_week: null };
  }
  return { ...form, day_of_week: null, day_of_month: null };
};

const getScheduleValidationError = (form: ReportSchedule): string | null => {
  if (form.schedule_type === 'weekly' && !form.day_of_week) {
    return 'Select a day of week.';
  }
  if (form.schedule_type === 'monthly' && !form.day_of_month) {
    return 'Select a day of month.';
  }
  return null;
};

function ReportProfilesComponent({ locationId, ssoToken }: ReportProfilesProps) {
  // List state
  const [profiles, setProfiles] = useState<ReportProfile[]>([]);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selected profile for editing
  const [selectedProfile, setSelectedProfile] = useState<ReportProfile | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Form states for selected profile
  const [formData, setFormData] = useState({
    profile_name: '',
    report_type: 'opportunities' as 'opportunities' | 'notes',
    is_active: true,
    notes: '',
  });

  // Schedule state
  const [scheduleForm, setScheduleForm] = useState<ReportSchedule>({ ...DEFAULT_SCHEDULE_FORM });

  // Recipients state
  const [recipients, setRecipients] = useState<ReportRecipient[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [recipientError, setRecipientError] = useState<string | null>(null);

  // Pipeline stages state (for opportunities)
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [pipelineConfigs, setPipelineConfigs] = useState<PipelineStageConfig[]>([]);

  // Custom fields state (for opportunities)
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [customFieldConfigs, setCustomFieldConfigs] = useState<CustomFieldConfig[]>([]);

  // Notes config state
  const [notesConfig, setNotesConfig] = useState<NotesConfig>({ ...DEFAULT_NOTES_CONFIG });

  // Loading states
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const headers = { 'X-GHL-SSO': ssoToken };

  // Load profiles list
  const loadProfiles = useCallback(async () => {
    try {
      setIsLoadingProfiles(true);
      setError(null);
      const response = await Api.get('/ghl/embed/report-profiles', { headers });
      const data = response?.data?.data || response?.data || [];
      setProfiles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading report profiles:', err);
      setError('Failed to load report profiles');
    } finally {
      setIsLoadingProfiles(false);
    }
  }, [ssoToken]);

  // Load pipelines and custom fields
  const loadPipelinesAndFields = useCallback(async () => {
    try {
      const [pipelinesRes, fieldsRes] = await Promise.all([
        Api.get('/ghl/embed/pipelines', { params: { locationId }, headers }),
        Api.get('/ghl/embed/custom-fields', { params: { locationId }, headers }),
      ]);

      const pipelinesData = pipelinesRes?.data?.pipelines || pipelinesRes?.data?.data || [];
      // Map pipelines with stages to ensure correct structure
      const mappedPipelines: Pipeline[] = (Array.isArray(pipelinesData) ? pipelinesData : []).map((pipeline: any) => ({
        id: pipeline.id,
        name: pipeline.name || 'Unknown',
        stages: (pipeline.stages || [])
          .sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
          .map((stage: any) => ({
            id: stage.id,
            name: stage.name || stage.label || 'Unknown',
          })),
      }));
      setPipelines(mappedPipelines);

      const fieldsData = fieldsRes?.data?.customFields || fieldsRes?.data?.data || [];
      setCustomFields(Array.isArray(fieldsData) ? fieldsData : []);
    } catch (err) {
      console.error('Error loading pipelines/fields:', err);
    }
  }, [locationId, ssoToken]);

  // Load profile details
  const loadProfileDetails = useCallback(
    async (profileId: string, reportType: ReportProfile['report_type']) => {
      setIsLoadingDetails(true);
      setRecipients([]);
      setPipelineConfigs([]);
      setCustomFieldConfigs([]);
      setNotesConfig({ ...DEFAULT_NOTES_CONFIG });
      setScheduleForm({ ...DEFAULT_SCHEDULE_FORM });
      setNewEmail('');
      setRecipientError(null);

      try {
        const wrapRequest = (promise: Promise<any>) =>
          promise
            .then((value) => ({ ok: true, value }))
            .catch((error) => ({ ok: false, error }));

        const [scheduleResult, recipientsResult, pipelineConfigsResult, customFieldConfigsResult, notesConfigResult] = await Promise.all([
          wrapRequest(Api.get(`/ghl/embed/report-profiles/${profileId}/schedule`, { headers })),
          wrapRequest(Api.get(`/ghl/embed/report-profiles/${profileId}/recipients`, { headers })),
          reportType === 'opportunities'
            ? wrapRequest(Api.get(`/ghl/embed/report-profiles/${profileId}/pipeline-configs`, { headers }))
            : wrapRequest(Promise.resolve(null)),
          reportType === 'opportunities'
            ? wrapRequest(Api.get(`/ghl/embed/report-profiles/${profileId}/custom-field-configs`, { headers }))
            : wrapRequest(Promise.resolve(null)),
          reportType === 'notes'
            ? wrapRequest(Api.get(`/ghl/embed/report-profiles/${profileId}/notes-config`, { headers }))
            : wrapRequest(Promise.resolve(null)),
        ]);

        if (scheduleResult.ok) {
          const scheduleData = scheduleResult.value?.data?.data || scheduleResult.value?.data;
          if (scheduleData) {
            setScheduleForm({
              schedule_type: scheduleData.schedule_type || 'daily',
              time: scheduleData.time || '09:00',
              timezone: scheduleData.timezone || 'America/New_York',
              day_of_week: scheduleData.day_of_week || null,
              day_of_month: scheduleData.day_of_month || null,
              is_active: scheduleData.is_active ?? true,
            });
          }
        } else {
          console.error('Error loading report schedule:', scheduleResult.error);
        }

        if (recipientsResult.ok) {
          const recipientsData = recipientsResult.value?.data?.data || recipientsResult.value?.data || [];
          setRecipients(Array.isArray(recipientsData) ? recipientsData : []);
        } else {
          console.error('Error loading report recipients:', recipientsResult.error);
        }

        if (pipelineConfigsResult.ok) {
          const pipelineConfigsData = pipelineConfigsResult.value?.data?.data || pipelineConfigsResult.value?.data || [];
          setPipelineConfigs(Array.isArray(pipelineConfigsData) ? pipelineConfigsData : []);
        } else {
          console.error('Error loading pipeline configs:', pipelineConfigsResult.error);
        }

        if (customFieldConfigsResult.ok) {
          const customFieldConfigsData = customFieldConfigsResult.value?.data?.data || customFieldConfigsResult.value?.data || [];
          setCustomFieldConfigs(Array.isArray(customFieldConfigsData) ? customFieldConfigsData : []);
        } else {
          console.error('Error loading custom field configs:', customFieldConfigsResult.error);
        }

        if (notesConfigResult.ok) {
          const notesData = notesConfigResult.value?.data?.data || notesConfigResult.value?.data;
          if (notesData) {
            setNotesConfig({ time_range_hours: notesData.time_range_hours || 24 });
          }
        } else {
          console.error('Error loading notes config:', notesConfigResult.error);
        }
      } catch (err) {
        console.error('Error loading profile details:', err);
      } finally {
        setIsLoadingDetails(false);
      }
    },
    [ssoToken]
  );

  useEffect(() => {
    loadProfiles();
    loadPipelinesAndFields();
  }, [loadProfiles, loadPipelinesAndFields]);

  useEffect(() => {
    if (selectedProfile) {
      setFormData({
        profile_name: selectedProfile.profile_name,
        report_type: selectedProfile.report_type,
        is_active: selectedProfile.is_active,
        notes: selectedProfile.notes || '',
      });
      loadProfileDetails(selectedProfile.id, selectedProfile.report_type);
    }
  }, [selectedProfile, loadProfileDetails]);

  // Create new profile
  const handleCreate = async () => {
    try {
      setIsSaving(true);
      const response = await Api.post('/ghl/embed/report-profiles', formData, { headers });
      const newProfile = response?.data?.data || response?.data;
      setProfiles((prev) => [...prev, newProfile]);
      setSelectedProfile(newProfile);
      setIsCreating(false);
    } catch (err) {
      console.error('Error creating profile:', err);
      setError(getApiErrorMessage(err, 'Failed to create profile'));
    } finally {
      setIsSaving(false);
    }
  };

  // Update profile
  const handleUpdate = async () => {
    if (!selectedProfile) return;
    try {
      setIsSaving(true);
      await Api.put(`/ghl/embed/report-profiles/${selectedProfile.id}`, formData, { headers });
      setProfiles((prev) =>
        prev.map((p) => (p.id === selectedProfile.id ? { ...p, ...formData } : p))
      );
      setSelectedProfile((prev) => (prev ? { ...prev, ...formData } : null));
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(getApiErrorMessage(err, 'Failed to update profile'));
    } finally {
      setIsSaving(false);
    }
  };

  // Delete profile
  const handleDelete = async () => {
    if (!selectedProfile || !window.confirm('Are you sure you want to delete this profile?')) return;
    try {
      setIsSaving(true);
      await Api.delete(`/ghl/embed/report-profiles/${selectedProfile.id}`, { headers });
      setProfiles((prev) => prev.filter((p) => p.id !== selectedProfile.id));
      setSelectedProfile(null);
    } catch (err) {
      console.error('Error deleting profile:', err);
      setError('Failed to delete profile');
    } finally {
      setIsSaving(false);
    }
  };

  // Save schedule
  const handleSaveSchedule = async () => {
    if (!selectedProfile) return;
    if (getScheduleValidationError(scheduleForm)) return;
    try {
      setIsSaving(true);
      const payload = normalizeScheduleForm(scheduleForm);
      await Api.put(`/ghl/embed/report-profiles/${selectedProfile.id}/schedule`, payload, { headers });
    } catch (err) {
      console.error('Error saving schedule:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Add recipient
  const handleAddRecipient = async () => {
    if (!selectedProfile) return;
    const trimmedEmail = newEmail.trim();
    if (!trimmedEmail) return;
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      return;
    }
    try {
      const response = await Api.post(
        `/ghl/embed/report-profiles/${selectedProfile.id}/recipients`,
        { email: trimmedEmail },
        { headers }
      );
      const newRecipient = response?.data?.data || response?.data;
      setRecipients((prev) => [...prev, newRecipient]);
      setNewEmail('');
      setRecipientError(null);
    } catch (err) {
      console.error('Error adding recipient:', err);
      setRecipientError('Failed to add recipient.');
    }
  };

  // Remove recipient
  const handleRemoveRecipient = async (recipientId: string) => {
    if (!selectedProfile) return;
    try {
      await Api.delete(`/ghl/embed/report-profiles/${selectedProfile.id}/recipients/${recipientId}`, { headers });
      setRecipients((prev) => prev.filter((r) => r.id !== recipientId));
    } catch (err) {
      console.error('Error removing recipient:', err);
    }
  };

  // Toggle pipeline stage (auto-saves immediately)
  const handleTogglePipelineStage = async (pipelineId: string, stageId: string) => {
    if (!selectedProfile) return;

    // Calculate new configs
    const existing = pipelineConfigs.find((c) => c.pipeline_id === pipelineId && c.pipeline_stage_id === stageId);
    let newConfigs: PipelineStageConfig[];
    if (existing) {
      newConfigs = pipelineConfigs.map((c) =>
        c.pipeline_id === pipelineId && c.pipeline_stage_id === stageId
          ? { ...c, include_in_reports: !c.include_in_reports }
          : c
      );
    } else {
      newConfigs = [...pipelineConfigs, { pipeline_id: pipelineId, pipeline_stage_id: stageId, include_in_reports: true }];
    }

    // Update local state immediately for responsive UI
    setPipelineConfigs(newConfigs);

    // Save to API
    try {
      await Api.put(
        `/ghl/embed/report-profiles/${selectedProfile.id}/pipeline-configs/bulk`,
        { configs: newConfigs },
        { headers }
      );
    } catch (err) {
      console.error('Error saving pipeline configs:', err);
      // Revert on error
      setPipelineConfigs(pipelineConfigs);
    }
  };

  // Toggle custom field
  const handleToggleCustomField = async (fieldId: string) => {
    if (!selectedProfile) return;
    const existing = customFieldConfigs.find((c) => c.custom_field_id === fieldId);
    try {
      if (existing) {
        await Api.delete(`/ghl/embed/report-profiles/${selectedProfile.id}/custom-field-configs/${existing.id}`, { headers });
        setCustomFieldConfigs((prev) => prev.filter((c) => c.id !== existing.id));
      } else {
        const response = await Api.post(
          `/ghl/embed/report-profiles/${selectedProfile.id}/custom-field-configs`,
          { custom_field_id: fieldId },
          { headers }
        );
        const newConfig = response?.data?.data || response?.data;
        setCustomFieldConfigs((prev) => [...prev, newConfig]);
      }
    } catch (err) {
      console.error('Error toggling custom field:', err);
    }
  };

  // Save notes config
  const handleSaveNotesConfig = async () => {
    if (!selectedProfile) return;
    try {
      setIsSaving(true);
      await Api.put(`/ghl/embed/report-profiles/${selectedProfile.id}/notes-config`, notesConfig, { headers });
    } catch (err) {
      console.error('Error saving notes config:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Send report now
  const handleSendNow = async () => {
    if (!selectedProfile) return;
    try {
      setIsSaving(true);
      await Api.post(`/ghl/embed/report-profiles/${selectedProfile.id}/send-report`, {}, { headers });
      alert('Report sent successfully!');
    } catch (err) {
      console.error('Error sending report:', err);
      alert('Failed to send report');
    } finally {
      setIsSaving(false);
    }
  };

  // Start creating new profile
  const startCreate = () => {
    setSelectedProfile(null);
    setIsCreating(true);
    setFormData({
      profile_name: '',
      report_type: 'opportunities',
      is_active: true,
      notes: '',
    });
  };

  // Cancel create/edit
  const handleCancel = () => {
    setIsCreating(false);
    setSelectedProfile(null);
  };

  const trimmedEmail = newEmail.trim();
  const isRecipientEmailFilled = trimmedEmail.length > 0;
  const isRecipientEmailValid = isRecipientEmailFilled && EMAIL_REGEX.test(trimmedEmail);
  const recipientValidationMessage = recipientError
    || (isRecipientEmailFilled && !isRecipientEmailValid ? 'Enter a valid email address.' : null);
  const scheduleValidationError = getScheduleValidationError(scheduleForm);

  if (isLoadingProfiles) {
    return (
      <div className={classes.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading report profiles...</p>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className="row">
        {/* Left Panel - Profiles List */}
        <div className="col-md-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Report Profiles</h5>
            <button className="btn btn-primary btn-sm" onClick={startCreate}>
              + New Profile
            </button>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          <div className="list-group">
            {profiles.length === 0 ? (
              <div className="text-muted p-3">No profiles yet. Create one to get started.</div>
            ) : (
              profiles.map((profile) => (
                <button
                  key={profile.id}
                  type="button"
                  className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                    selectedProfile?.id === profile.id ? 'active' : ''
                  }`}
                  onClick={() => {
                    setIsCreating(false);
                    setSelectedProfile(profile);
                  }}
                >
                  <div>
                    <div className="fw-bold">{profile.profile_name}</div>
                    <small className={selectedProfile?.id === profile.id ? 'text-white-50' : 'text-muted'}>
                      {profile.report_type}
                    </small>
                  </div>
                  <span className={`badge ${profile.is_active ? 'bg-success' : 'bg-secondary'}`}>
                    {profile.is_active ? 'Active' : 'Inactive'}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right Panel - Edit Form */}
        <div className="col-md-8">
          {!selectedProfile && !isCreating ? (
            <div className="text-center text-muted p-5">
              Select a profile to edit or create a new one
            </div>
          ) : (
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h6 className="mb-0">{isCreating ? 'New Profile' : 'Edit Profile'}</h6>
                {selectedProfile && (
                  <button className="btn btn-outline-danger btn-sm" onClick={handleDelete} disabled={isSaving}>
                    Delete
                  </button>
                )}
              </div>
              <div className="card-body">
                {isLoadingDetails ? (
                  <div className="text-center p-3">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <span className="ms-2">Loading...</span>
                  </div>
                ) : (
                  <>
                    {/* Basic Info */}
                    <div className="mb-4">
                      <h6 className="border-bottom pb-2">Basic Info</h6>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">Profile Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.profile_name}
                            onChange={(e) => setFormData({ ...formData, profile_name: e.target.value })}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Report Type</label>
                          <select
                            className="form-select"
                            value={formData.report_type}
                            onChange={(e) => setFormData({ ...formData, report_type: e.target.value as 'opportunities' | 'notes' })}
                          >
                            <option value="opportunities">Opportunities</option>
                            <option value="notes">Notes</option>
                          </select>
                        </div>
                        <div className="col-12">
                          <label className="form-label">Notes</label>
                          <textarea
                            className="form-control"
                            rows={2}
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          />
                        </div>
                        <div className="col-12">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="isActive"
                              checked={formData.is_active}
                              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            />
                            <label className="form-check-label" htmlFor="isActive">Active</label>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3">
                        <button
                          className="btn btn-primary me-2"
                          onClick={isCreating ? handleCreate : handleUpdate}
                          disabled={isSaving || !formData.profile_name}
                        >
                          {isSaving ? 'Saving...' : isCreating ? 'Create Profile' : 'Save Changes'}
                        </button>
                        <button className="btn btn-secondary" onClick={handleCancel}>
                          Cancel
                        </button>
                      </div>
                    </div>

                    {/* Only show additional sections for existing profiles */}
                    {selectedProfile && (
                      <>
                        {/* Schedule */}
                        <div className="mb-4">
                          <h6 className="border-bottom pb-2">Schedule</h6>
                          <div className="row g-3">
                            <div className="col-md-4">
                              <label className="form-label">Type</label>
                              <select
                                className="form-select"
                                value={scheduleForm.schedule_type}
                                onChange={(e) => setScheduleForm({ ...scheduleForm, schedule_type: e.target.value as 'daily' | 'weekly' | 'monthly' })}
                              >
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                              </select>
                            </div>
                            <div className="col-md-4">
                              <label className="form-label">Time</label>
                              <input
                                type="time"
                                className="form-control"
                                value={scheduleForm.time}
                                onChange={(e) => setScheduleForm({ ...scheduleForm, time: e.target.value })}
                              />
                            </div>
                            <div className="col-md-4">
                              <label className="form-label">Timezone</label>
                              <select
                                className="form-select"
                                value={scheduleForm.timezone}
                                onChange={(e) => setScheduleForm({ ...scheduleForm, timezone: e.target.value })}
                              >
                                {TIMEZONES.map((tz) => (
                                  <option key={tz} value={tz}>{tz}</option>
                                ))}
                              </select>
                            </div>
                            {scheduleForm.schedule_type === 'weekly' && (
                              <div className="col-md-6">
                                <label className="form-label">Day of Week</label>
                                <select
                                  className="form-select"
                                  value={scheduleForm.day_of_week || ''}
                                  onChange={(e) => setScheduleForm({ ...scheduleForm, day_of_week: e.target.value || null })}
                                >
                                  <option value="">Select day</option>
                                  {DAYS_OF_WEEK.map((day) => (
                                    <option key={day} value={day}>{day}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                            {scheduleForm.schedule_type === 'monthly' && (
                              <div className="col-md-6">
                                <label className="form-label">Day of Month</label>
                                <select
                                  className="form-select"
                                  value={scheduleForm.day_of_month || ''}
                                  onChange={(e) => setScheduleForm({ ...scheduleForm, day_of_month: e.target.value ? parseInt(e.target.value, 10) : null })}
                                >
                                  <option value="">Select day</option>
                                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                    <option key={day} value={day}>{day}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                            <div className="col-12">
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="scheduleActive"
                                  checked={scheduleForm.is_active}
                                  onChange={(e) => setScheduleForm({ ...scheduleForm, is_active: e.target.checked })}
                                />
                                <label className="form-check-label" htmlFor="scheduleActive">Schedule Active</label>
                              </div>
                            </div>
                          </div>
                          <button
                            className="btn btn-outline-primary btn-sm mt-2"
                            onClick={handleSaveSchedule}
                            disabled={isSaving || !!scheduleValidationError}
                          >
                            Save Schedule
                          </button>
                          {scheduleValidationError && <div className="text-danger mt-2">{scheduleValidationError}</div>}
                        </div>

                        {/* Recipients */}
                        <div className="mb-4">
                          <h6 className="border-bottom pb-2">Recipients</h6>
                          <div className="input-group mb-2" style={{ maxWidth: '400px' }}>
                            <input
                              type="email"
                              className="form-control"
                              placeholder="Add email..."
                              value={newEmail}
                              onChange={(e) => {
                                setNewEmail(e.target.value);
                                if (recipientError) {
                                  setRecipientError(null);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddRecipient();
                                }
                              }}
                              aria-invalid={recipientValidationMessage ? 'true' : 'false'}
                            />
                            <button className="btn btn-outline-primary" onClick={handleAddRecipient} disabled={isSaving || !isRecipientEmailValid}>
                              Add
                            </button>
                          </div>
                          {recipientValidationMessage && <div className="text-danger mb-2">{recipientValidationMessage}</div>}
                          {recipients.length === 0 ? (
                            <div className="text-muted">No recipients added yet</div>
                          ) : (
                            <ul className="list-group" style={{ maxWidth: '400px' }}>
                              {recipients.map((r) => (
                                <li key={r.id} className="list-group-item d-flex justify-content-between align-items-center">
                                  {r.email}
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleRemoveRecipient(r.id)}
                                  >
                                    &times;
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>

                        {/* Pipeline Stages (for opportunities) */}
                        {formData.report_type === 'opportunities' && (
                          <div className="mb-4">
                            <h6 className="border-bottom pb-2">Pipeline Stages</h6>
                            {pipelines.length === 0 ? (
                              <div className="text-muted">No pipelines available</div>
                            ) : (
                              pipelines.map((pipeline) => (
                                <div key={pipeline.id} className="mb-3">
                                  <div className="fw-bold mb-1">{pipeline.name}</div>
                                  <div className="ms-3">
                                    {pipeline.stages?.map((stage) => {
                                      const isChecked = pipelineConfigs.some(
                                        (c) => c.pipeline_id === pipeline.id && c.pipeline_stage_id === stage.id && c.include_in_reports
                                      );
                                      return (
                                        <div key={stage.id} className="form-check">
                                          <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id={`stage-${stage.id}`}
                                            checked={isChecked}
                                            onChange={() => handleTogglePipelineStage(pipeline.id, stage.id)}
                                          />
                                          <label className="form-check-label" htmlFor={`stage-${stage.id}`}>
                                            {stage.name}
                                          </label>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}

                        {/* Custom Fields (for opportunities) */}
                        {formData.report_type === 'opportunities' && (
                          <div className="mb-4">
                            <h6 className="border-bottom pb-2">Custom Fields</h6>
                            {customFields.length === 0 ? (
                              <div className="text-muted">No custom fields available</div>
                            ) : (
                              <div className="row">
                                {customFields.map((field) => {
                                  const isChecked = customFieldConfigs.some((c) => c.custom_field_id === field.id);
                                  return (
                                    <div key={field.id} className="col-md-6">
                                      <div className="form-check">
                                        <input
                                          type="checkbox"
                                          className="form-check-input"
                                          id={`field-${field.id}`}
                                          checked={isChecked}
                                          onChange={() => handleToggleCustomField(field.id)}
                                        />
                                        <label className="form-check-label" htmlFor={`field-${field.id}`}>
                                          {field.name}
                                        </label>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Notes Config (for notes) */}
                        {formData.report_type === 'notes' && (
                          <div className="mb-4">
                            <h6 className="border-bottom pb-2">Notes Configuration</h6>
                            <div className="row g-3">
                              <div className="col-md-6">
                                <label className="form-label">Time Range</label>
                                <select
                                  className="form-select"
                                  value={notesConfig.time_range_hours}
                                  onChange={(e) => setNotesConfig({ time_range_hours: parseInt(e.target.value, 10) })}
                                >
                                  {TIME_RANGE_PRESETS.map((preset) => (
                                    <option key={preset.value} value={preset.value}>{preset.label}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <button className="btn btn-outline-primary btn-sm mt-2" onClick={handleSaveNotesConfig} disabled={isSaving}>
                              Save Notes Config
                            </button>
                          </div>
                        )}

                        {/* Send Now */}
                        <div className="border-top pt-3">
                          <button className="btn btn-success" onClick={handleSendNow} disabled={isSaving}>
                            Send Report Now
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const ReportProfilesMemo = memo(ReportProfilesComponent, areEqual);

export { ReportProfilesMemo as ReportProfiles };
